import { useEffect, useState } from 'react';

import { httpAPI } from '@mirinae/react-auth';
import { ORG_SITE } from '../../defines/school';

/*
 *    The signed-in person's schools, for the shared header (#17 ④, classroom plan §9 C·H): the schools
 *    where they are a student or a teacher (an administrator's place is the admin app), each with their
 *    classes that are not archived and, for a student, the number of the school's notices they have not
 *    read. Read from the server whenever the header appears for a person -- each app is its own page load,
 *    so a notice read in the university app shows here as read on the next page.
 *
 *    `ready` is false until it is known. A request that failed leaves it not ready rather than saying "no
 *    school": the header then simply shows nothing extra, and never tells a school member they have none.
 *    Membership comes from /api/org/mine, not from `user.orgAccess`, which lists only the schools giving
 *    Pro right now (a student whose licence ended, or who is in no class, is still a member).
 */
const useSchools = (user) => {
    const userId = user && user.role !== 'guest' && user.loginType && user.loginType !== 'guest' ? String(user._id || '') : '';
    const [state, setState] = useState({ ready: false, schools: [], for: null });

    useEffect(() => {
        if (!ORG_SITE || !userId) {
            setState({ ready: true, schools: [], for: userId });
            return undefined;
        }
        let live = true;
        setState({ ready: false, schools: [], for: userId });
        (async () => {
            const { data: mine } = await httpAPI('', '/api/org/mine', { method: 'get' });
            const places = (mine || []).filter(m => ['student', 'teacher'].includes(m.orgRole));
            const schools = await Promise.all(places.map(async (m) => {
                const [classes, unread] = await Promise.all([
                    httpAPI('', `/api/org/${m._id}/classes`, { method: 'get' }).then(r => (r.data || []).filter(c => c.status !== 'archived')),
                    m.orgRole === 'student'
                        ? httpAPI('', `/api/org/${m._id}/notices/unread-count`, { method: 'get' }).then(r => r.data?.unread || 0, () => 0)
                        : Promise.resolve(0),
                ]);
                return { ...m, classes, unread };
            }));
            if (live) setState({ ready: true, schools, for: userId });
        })().catch(() => { /* not known: stays not ready */ });
        return () => { live = false; };
    }, [userId]);

    return state.for === userId ? state : { ready: false, schools: [], for: userId };
};

export default useSchools;
